import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft,
  Download,
  Check,
  Loader2,
  Monitor,
  Smartphone,
  Instagram,
  Youtube,
  FileVideo
} from 'lucide-react';
import { toast } from 'sonner';

const Export = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('mp4');
  const [selectedQuality, setSelectedQuality] = useState('1080p');
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const formats = [
    { id: 'mp4', name: 'MP4', desc: 'Most compatible' },
    { id: 'mov', name: 'MOV', desc: 'High quality' },
    { id: 'webm', name: 'WebM', desc: 'Web optimized' }
  ];

  const qualities = [
    { id: '720p', name: '720p HD', desc: 'Smaller file size' },
    { id: '1080p', name: '1080p Full HD', desc: 'Recommended', popular: true },
    { id: '4k', name: '4K Ultra HD', desc: 'Pro plan only', pro: true }
  ];

  const platforms = [
    { id: 'youtube', name: 'YouTube', icon: <Youtube className="w-5 h-5" />, ratio: '16:9' },
    { id: 'instagram', name: 'Instagram Reels', icon: <Instagram className="w-5 h-5" />, ratio: '9:16' },
    { id: 'tiktok', name: 'TikTok', icon: <Smartphone className="w-5 h-5" />, ratio: '9:16' },
    { id: 'desktop', name: 'Desktop', icon: <Monitor className="w-5 h-5" />, ratio: '16:9' }
  ];

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.get(projectId);
      setProject(response.data);
    } catch (error) {
      toast.error('Project not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    toast.success('Export complete! Your download will start shortly.');
    setExporting(false);
    
    // In production, this would trigger actual download
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white"
              onClick={() => navigate(`/preview/${projectId}`)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading font-semibold text-xl text-white">Export: {project?.title}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Platform Selection */}
            <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
              <h3 className="font-heading font-semibold text-white mb-2">Export For</h3>
              <p className="text-white/60 text-sm mb-6">Optimize your video for a specific platform</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {platforms.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className={`p-4 rounded-xl border transition-all ${
                      selectedPlatform === platform.id
                        ? 'bg-[#CCFF00]/10 border-[#CCFF00] text-[#CCFF00]'
                        : 'bg-[#121212] border-white/10 text-white hover:border-white/30'
                    }`}
                    data-testid={`platform-${platform.id}`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      {platform.icon}
                      <span className="font-medium text-sm">{platform.name}</span>
                      <span className="text-xs opacity-60">{platform.ratio}</span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Format Selection */}
            <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
              <h3 className="font-heading font-semibold text-white mb-2">Format</h3>
              <p className="text-white/60 text-sm mb-6">Choose your preferred video format</p>
              
              <div className="grid grid-cols-3 gap-4">
                {formats.map((format) => (
                  <button
                    key={format.id}
                    onClick={() => setSelectedFormat(format.id)}
                    className={`p-4 rounded-xl border transition-all ${
                      selectedFormat === format.id
                        ? 'bg-[#CCFF00]/10 border-[#CCFF00]'
                        : 'bg-[#121212] border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`format-${format.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-semibold ${selectedFormat === format.id ? 'text-[#CCFF00]' : 'text-white'}`}>
                          {format.name}
                        </p>
                        <p className="text-white/40 text-sm">{format.desc}</p>
                      </div>
                      {selectedFormat === format.id && (
                        <Check className="w-5 h-5 text-[#CCFF00]" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Quality Selection */}
            <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
              <h3 className="font-heading font-semibold text-white mb-2">Quality</h3>
              <p className="text-white/60 text-sm mb-6">Select video resolution</p>
              
              <div className="grid grid-cols-3 gap-4">
                {qualities.map((quality) => (
                  <button
                    key={quality.id}
                    onClick={() => !quality.pro && setSelectedQuality(quality.id)}
                    disabled={quality.pro}
                    className={`p-4 rounded-xl border transition-all relative ${
                      selectedQuality === quality.id
                        ? 'bg-[#CCFF00]/10 border-[#CCFF00]'
                        : quality.pro
                        ? 'bg-[#121212] border-white/5 opacity-50 cursor-not-allowed'
                        : 'bg-[#121212] border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`quality-${quality.id}`}
                  >
                    {quality.popular && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-[#CCFF00] text-black text-xs font-semibold rounded-full">
                        Popular
                      </span>
                    )}
                    {quality.pro && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-[#6E44FF] text-white text-xs font-semibold rounded-full">
                        Pro
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-semibold ${selectedQuality === quality.id ? 'text-[#CCFF00]' : 'text-white'}`}>
                          {quality.name}
                        </p>
                        <p className="text-white/40 text-sm">{quality.desc}</p>
                      </div>
                      {selectedQuality === quality.id && (
                        <Check className="w-5 h-5 text-[#CCFF00]" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Export Summary */}
            <Card className="bg-gradient-to-br from-[#0A0A0A] to-[#121212] border-white/10 p-6 rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-white mb-1">Export Summary</h3>
                  <div className="flex items-center gap-4 text-sm text-white/60">
                    <span>{selectedFormat.toUpperCase()}</span>
                    <span>•</span>
                    <span>{selectedQuality}</span>
                    {selectedPlatform && (
                      <>
                        <span>•</span>
                        <span className="capitalize">{selectedPlatform}</span>
                      </>
                    )}
                  </div>
                </div>
                <FileVideo className="w-10 h-10 text-[#CCFF00]" />
              </div>
              
              <Button
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-xl font-semibold py-6"
                onClick={handleExport}
                disabled={exporting}
                data-testid="export-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Export Video
                  </>
                )}
              </Button>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Export;
